import { useMemo } from "react"
import PropTypes from "prop-types"
import { chain, map } from "lodash"
import { Paper, Button, Box, Grid } from "@mui/material"
import { ERROR, OK } from "helpers/data-statuses"
import LoadingWrapper from "components/loading-wrapper"
import OptimizationLoading from "components/material/loading"
import OptimizationError from "components/material/error"
import ScorecardForecast from "./components/forecast"
import ScorecardDecompositionSummary from "./components/decomposition-summary"
import SelectedFactors from "./components/selected-factors"
import { CATEGORY, FACTOR } from "./shapes"
import FactorCategories from "components/factor-categories"
import SearchableMenu from "components/searchable-menu"
import { SEARCHABLE_MENU_ITEM } from "helpers/shapes"

import "./style.scss"

export default function OptimizationScorecard({
  status,
  forecast,
  rebalancedForecast,
  originalRiskDecomposition,
  rebalancedRiskDecomposition,
  factorCategories,
  selectedFactors,
  setDecompositionType,
  toggleSaveExperiment,
  saveVisible,
  decompositionType,
  scannerStatus,
  toggleTradesPanel,
  bestPointData,
  decompositionTypes,
}) {
  const onSelectDecompositionType = (id) => {
    setDecompositionType(id)
  }
  const loadingComponent = <OptimizationLoading />
  const errorComponent = scannerStatus !== ERROR
    ? <OptimizationError text="There was an error while loading this scorecard. Please try again." />
    : <div />

  const loadingStatus = scannerStatus === ERROR || (scannerStatus === OK && !bestPointData) ? ERROR : status

  // To be able to use the common FactorCategories, we need to map getters both at the category and factor level
  const categories = useMemo(() => {
    const makeValueGetter = item => () => {
      const { formatter } = item
      return chain(item)
      // Can't use pick here because we need to guarantee the order of the properties
        .thru(({ rebalanced, original, delta }) => [rebalanced, original, delta])
        .map(value => value ? formatter(value) : value)
        .value()
    }

    return map(factorCategories, (category) => ({
      ...category,
      factors: chain(category)
        .get("factors")
        .map(factor => ({
          ...factor,
          getter: makeValueGetter(factor),
        }))
        .value(),
      getter: makeValueGetter(category),
    }),
    )
  }, [factorCategories])

  return <div className="modules_optimization-scorecard">
    <LoadingWrapper status={[loadingStatus]} loadingComponent={loadingComponent} errorComponent={errorComponent}>
      <Paper className="chart-container">
        <Grid container className="header">
          <Grid item className="metric">Forecast</Grid>
          <Grid container item className="values">
            <div className="optimizer">Optimized</div>
            <div className="original">Original</div>
            <div className="delta">Delta</div>
          </Grid>
        </Grid>
        <ScorecardForecast forecast={forecast} rebalancedForecast={rebalancedForecast} />
        <div style={{ display: "flex", flexDirection: "row-reverse" }}>
          <Button onClick={toggleTradesPanel} variant="contained" color="primary">Trades</Button>
          {saveVisible && <Button onClick={toggleSaveExperiment} className="save-compare" variant="contained" color="primary">Save Experiment</Button>}
        </div>

        <Box className="details">
          <Grid container className="header">
            <SearchableMenu current={decompositionType} items={decompositionTypes} onSelect={(item) => onSelectDecompositionType(item.id)} hideSearchBox className="optimization-scorecard-select-menu" />
            <Grid container item className="values">
              <div className="optimizer">Optimized</div>
              <div className="original">Original</div>
              <div className="delta">Delta</div>
            </Grid>
          </Grid>
          {decompositionType === "risk"
            ? <ScorecardDecompositionSummary
                originalRiskDecomposition={originalRiskDecomposition}
                rebalancedRiskDecomposition={rebalancedRiskDecomposition}
            /> : <SelectedFactors selectedFactors={selectedFactors} />}
          {/* getValues: Since we declared the getter function inside this component, we know that we only need to execute the getter to generate the correct values */}
          <FactorCategories {...{ categories, getValues: (getter) => getter(), valueIds: ["optimizer", "original", "delta"] }} />
        </Box>
      </Paper>
    </LoadingWrapper>
  </div>
}

OptimizationScorecard.propTypes = {
  status: PropTypes.string.isRequired,
  forecast: PropTypes.object.isRequired,
  rebalancedForecast: PropTypes.object.isRequired,
  originalRiskDecomposition: PropTypes.object.isRequired,
  rebalancedRiskDecomposition: PropTypes.object.isRequired,
  factorCategories: PropTypes.arrayOf(CATEGORY).isRequired,
  selectedFactors: PropTypes.arrayOf(FACTOR).isRequired,
  setDecompositionType: PropTypes.func.isRequired,
  toggleSaveExperiment: PropTypes.func.isRequired,
  saveVisible: PropTypes.bool.isRequired,
  decompositionType: PropTypes.string.isRequired,
  scannerStatus: PropTypes.string,
  toggleTradesPanel: PropTypes.func.isRequired,
  bestPointData: PropTypes.object,
  decompositionTypes: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM).isRequired,
  selectedDecompositionTypeName: PropTypes.string.isRequired,
}
